import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, query, where } from 'firebase/firestore'; // Removed orderBy
import { 
  Plus, Search, Edit, Trash2, Users, DollarSign, Calendar, TrendingUp, 
  LogOut, X, Globe, Loader, Check, Clock, Phone, AlertCircle, User, CalendarDays, Copy, CheckCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import DarkModeToggle from './DarkModeToggle';
import HjezlyLogo from './HjezlyLogo';

const SuperAdminPanel = () => {
  const { t, i18n } = useTranslation();
  const [stadiums, setStadiums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for Add Modal
  const [showAddStadium, setShowAddStadium] = useState(false);
  const [newStadium, setNewStadium] = useState({
    id: '', name: '', location: '', phone: '',
    pricePerSlot: 30, ownerEmail: '', monthlyFee: 0
  });
  const [createdOwnerCredentials, setCreatedOwnerCredentials] = useState(null); // Store { email, password }

  // State for Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStadium, setSelectedStadium] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // 'idle', 'saving', 'success'

  // State for Bookings
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    loadStadiums();
  }, []);

  useEffect(() => {
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const loadStadiums = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'stadiums'));
      const stadiumsData = [];
      querySnapshot.forEach((doc) => {
        stadiumsData.push({ id: doc.id, ...doc.data() });
      });
      setStadiums(stadiumsData);
    } catch (error) {
      console.error('Error loading stadiums:', error);
    }
    setLoading(false);
  };

  const loadBookings = async () => {
    setLoadingBookings(true);
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      
      // 1. We ONLY filter by date. We remove the orderBy('startTime').
      const q = query(
        collection(db, 'bookings'),
        where('date', '==', dateString)
      );
      
      const querySnapshot = await getDocs(q);
      const bookingsData = [];
      querySnapshot.forEach((doc) => {
        bookingsData.push({ id: doc.id, ...doc.data() });
      });
      
      // 2. We sort the results here, in JavaScript, after fetching.
      bookingsData.sort((a, b) => a.startTime.localeCompare(b.startTime));

      setBookings(bookingsData);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
    setLoadingBookings(false);
  };

  const handleAddStadium = async () => {
    if (!newStadium.id || !newStadium.name || !newStadium.location || !newStadium.ownerEmail) {
      toast.error(t('superadminpanel.errorFields'));
      return;
    }

    // Show loading state
    const loadingToast = toast.loading('Creating stadium and owner account...');

    try {
      // Step 1: Create the stadium in Firestore
      const stadiumData = {
        id: newStadium.id,
        name: newStadium.name,
        location: newStadium.location,
        ownerEmail: newStadium.ownerEmail,
        city: newStadium.location.split(',')[0] || 'Unknown',
        phone: newStadium.phone || '+961 X XXX XXX',
        description: 'A brand new stadium on Hjezly!',
        amenities: ['Free Parking', 'Changing Rooms', 'Night Lights'],
        photos: [
          'https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=1200'
        ],
        rating: 4.0,
        reviewCount: 0,
        featured: false,
        coordinates: { lat: 33.8938, lng: 35.5018 },
        monthlyFee: parseInt(newStadium.monthlyFee) || 0,
        createdAt: new Date().toISOString(),
        status: 'active',
        // Default cancellation policy (stadium-specific)
        cancellationPolicy: {
          type: 'pay-at-venue',
          requiresPayment: false,
          cancellationWindow: 36,
          refundPercentage: 100,
          lateRefundPercentage: 0,
          lateCancellationFee: 50
        },
        sports: [
          {
            id: 'football-10',
            name: 'Football Field (10 players)',
            type: 'football',
            capacity: 10,
            pricePerSlot: parseInt(newStadium.pricePerSlot) || 30,
            openTime: '08:00',
            closeTime: '23:00',
            duration: 90,
            size: '35x18m'
          }
        ]
      };
      
      await setDoc(doc(db, 'stadiums', newStadium.id), stadiumData);

      // Step 2: Create Firebase Auth account for owner using Cloud Function
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const createOwnerAccount = httpsCallable(functions, 'createOwnerAccount');
        
        const result = await createOwnerAccount({
          email: newStadium.ownerEmail,
          stadiumName: newStadium.name
        });

        if (result.data.success) {
          // Success! Show credentials to admin
          setCreatedOwnerCredentials({
            email: result.data.email,
            password: result.data.password,
            stadiumName: newStadium.name
          });
          
          toast.dismiss(loadingToast);
          toast.success('✅ Stadium and owner account created successfully!');
        } else if (result.data.code === 'already-exists') {
          // Account already exists, that's okay
          toast.dismiss(loadingToast);
          toast.info('Stadium created! Owner account already exists.');
          setShowAddStadium(false);
        } else {
          throw new Error(result.data.error || 'Unknown error');
        }
      } catch (functionError) {
        // If Cloud Function fails, stadium is still created
        console.error('Error creating owner account:', functionError);
        toast.dismiss(loadingToast);
        toast.warning('Stadium created, but failed to create owner account. Please create it manually in Firebase Console.');
        setShowAddStadium(false);
      }

      // Reset form (keep modal open if credentials were created to show them)
      setNewStadium({
        id: '', name: '', location: '', phone: '',
        pricePerSlot: 30, ownerEmail: '', monthlyFee: 0
      });
      loadStadiums();

    } catch (error) {
      console.error('Error adding stadium:', error);
      toast.dismiss(loadingToast);
      toast.error(t('superadminpanel.errorAdd'));
    }
  };

  const handleDeleteStadium = async (stadiumId, stadiumName) => {
    if (window.confirm(`Are you sure you want to delete ${stadiumName}? This cannot be undone!`)) {
      try {
        await deleteDoc(doc(db, 'stadiums', stadiumId));
        toast.success(t('superadminpanel.stadiumDeleted'));
        loadStadiums();
      } catch (error) {
        console.error('Error deleting stadium:', error);
        toast.error(t('superadminpanel.errorDelete'));
      }
    }
  };

  const handleOpenEditModal = (stadium) => {
    setSelectedStadium(stadium);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedStadium(null);
    setSaveState('idle'); // Reset the save state
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setSelectedStadium(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateStadium = async () => {
    if (!selectedStadium) return;
    setSaveState('saving');
    try {
      const docRef = doc(db, 'stadiums', selectedStadium.id);
      const dataToSave = {
        ...selectedStadium,
        monthlyFee: parseInt(selectedStadium.monthlyFee) || 0,
      };
      await setDoc(docRef, dataToSave, { merge: true }); 
      setSaveState('success');
      loadStadiums();
      setTimeout(() => {
        handleCloseEditModal();
      }, 1500);
    } catch (error) {
      console.error('Error updating stadium:', error);
      toast.error(t('superadminpanel.errorUpdate'));
      setSaveState('idle');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const isToday = selectedDate.toDateString() === new Date().toDateString();
  const totalBookingRevenue = bookings.reduce((sum, b) => sum + (b.price || 0), 0);
  const totalSubscriptionRevenue = stadiums.reduce((sum, s) => sum + (s.monthlyFee || 0), 0);

  const filteredStadiums = stadiums.filter(stadium =>
    (stadium.name && stadium.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (stadium.location && stadium.location.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (stadium.ownerEmail && stadium.ownerEmail.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900">
      {/* Header */}
      <div className="bg-primary dark:bg-primary-900 text-white p-6 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold mb-2">{t('superadminpanel.title')}</h1>
              <p className="text-white/90">{t('superadminpanel.subtitle')}</p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en')}
                className="flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white px-5 py-3 rounded-xl font-semibold transition border border-white/20"
              >
                <Globe size={18} />
                {i18n.language === 'en' ? 'العربية' : 'English'}
              </button>
              <DarkModeToggle />
              <button 
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 py-3 mt-4 text-red-400 hover:text-red-300 hover:bg-white/5 rounded-xl transition-all"
              >
                <LogOut size={18} />
                {t('superadminpanel.logout')}
              </button>
            </div>
            {/* Logo */}
            <div className="flex justify-center mt-3 mb-2">
              <HjezlyLogo size="sm" variant="icon" />
            </div>
          </div>
        </div>
      </div>

      {/* --- Main Content --- */}
      <div className="flex-1 overflow-auto">
        {/* Stats */}
        <div className="max-w-7xl mx-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-neutral-800 p-6 rounded-xl shadow-md border-l-4 border-primary">
              <div className="flex justify-between items-start mb-3">
                <div className="text-neutral-600 dark:text-neutral-400 text-sm font-medium">{t('superadminpanel.totalStadiums')}</div>
                <Globe size={24} className="text-primary dark:text-primary-light" />
              </div>
              <div className="text-3xl font-bold text-neutral-800 dark:text-white">{stadiums.length}</div>
              <div className="text-sm text-primary dark:text-primary-light mt-2">
                {stadiums.filter(s => s.status === 'active').length} {t('superadminpanel.activeClients')}
              </div>
            </div>
            <div className="bg-white dark:bg-neutral-800 p-6 rounded-xl shadow-md border-l-4 border-blue-500">
              <div className="flex justify-between items-start mb-3">
                <div className="text-neutral-600 dark:text-neutral-400 text-sm font-medium">{t('superadminpanel.monthlyRevenue')}</div>
                <DollarSign size={24} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-3xl font-bold text-neutral-800 dark:text-white">${totalSubscriptionRevenue}</div>
              <div className="text-sm text-blue-600 dark:text-blue-400 mt-2">{t('superadminpanel.fromSubscriptions')}</div>
            </div>
            <div className="bg-white dark:bg-neutral-800 p-6 rounded-xl shadow-md border-l-4 border-purple-500">
              <div className="flex justify-between items-start mb-3">
                <div className="text-neutral-600 dark:text-neutral-400 text-sm font-medium">{t('superadminpanel.bookings')} ({isToday ? t('superadminpanel.today') : t('superadminpanel.selected')})</div>
                <Calendar size={24} className="text-purple-600 dark:text-purple-400" />
              </div>
              <div className="text-3xl font-bold text-neutral-800 dark:text-white">{bookings.length}</div>
              <div className="text-sm text-purple-600 dark:text-purple-400 mt-2">{t('superadminpanel.totalBookings')} {isToday ? t('superadminpanel.today') : t('superadminpanel.selectedDate')}</div>
            </div>
            <div className="bg-white dark:bg-neutral-800 p-6 rounded-xl shadow-md border-l-4 border-orange-500">
              <div className="flex justify-between items-start mb-3">
                <div className="text-neutral-600 dark:text-neutral-400 text-sm font-medium">{t('superadminpanel.revenue')} ({isToday ? t('superadminpanel.today') : t('superadminpanel.selected')})</div>
                <TrendingUp size={24} className="text-orange-500 dark:text-orange-400" />
              </div>
              <div className="text-3xl font-bold text-neutral-800 dark:text-white">${totalBookingRevenue}</div>
              <div className="text-sm text-orange-600 dark:text-orange-400 mt-2">{t('superadminpanel.fromBookings')} {isToday ? t('superadminpanel.today') : t('superadminpanel.onSelectedDate')}</div>
            </div>
          </div>

          {/* Stadiums List */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md p-6 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-bold text-neutral-800 dark:text-white mb-1">{t('superadminpanel.stadiumClients')}</h2>
                <p className="text-neutral-600 dark:text-neutral-400">{t('superadminpanel.manageClients')}</p>
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:flex-initial">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
                  <input
                    type="text"
                    placeholder={t('superadminpanel.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent w-full md:w-64"
                  />
                </div>
                <button
                  onClick={() => setShowAddStadium(true)}
                  className="flex items-center gap-2 bg-primary text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-dark transition whitespace-nowrap"
                >
                  <Plus size={20} />
                  {t('superadminpanel.addStadium')}
                </button>
              </div>
            </div>
            {loading ? (
              <div className="text-center py-8 text-neutral-600 dark:text-neutral-400">{t('superadminpanel.loadingStadiums')}</div>
            ) : filteredStadiums.length === 0 ? (
              <div className="text-center py-12">
                <Users size={48} className="mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
                <p className="text-neutral-500 dark:text-neutral-400 text-lg">{t('superadminpanel.noStadiums')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-neutral-200 dark:border-neutral-700">
                      <th className="text-left py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">{t('superadminpanel.stadium')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">{t('superadminpanel.ownerEmail')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">{t('superadminpanel.mrr')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">{t('superadminpanel.status')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">{t('superadminpanel.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStadiums.map((stadium) => (
                      <tr key={stadium.id} className="border-b border-neutral-100 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition">
                        <td className="py-4 px-4">
                          <div>
                            <div className="font-semibold text-neutral-800 dark:text-white">{stadium.name}</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">{stadium.location}</div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="text-neutral-700 dark:text-neutral-300">{stadium.ownerEmail}</div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="font-semibold text-blue-600 dark:text-blue-400">${stadium.monthlyFee || 0}</div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            stadium.status === 'active'
                              ? 'bg-primary/20 dark:bg-primary/30 text-primary-700 dark:text-primary-300'
                              : 'bg-accent/20 dark:bg-accent/30 text-accent-700 dark:text-accent-300'
                          }`}>
                            {stadium.status || 'active'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleOpenEditModal(stadium)}
                              className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition"
                              title={t('superadminpanel.edit')}
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteStadium(stadium.id, stadium.name)}
                              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
                              title={t('superadminpanel.delete')}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* --- BOOKINGS SECTION --- */}
          <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6 mt-8">
            <h2 className="text-2xl font-bold text-neutral-800 dark:text-white mb-6">{t('superadminpanel.platformBookings')}</h2>
            
            <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 mb-6 border-2 border-neutral-100 dark:border-neutral-700">
              <div className="flex items-center justify-center gap-4">
                <button 
                  onClick={() => changeDate(-1)}
                  className="w-12 h-12 flex items-center justify-center bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-xl transition"
                >
                  ←
                </button>
                <div className="text-center bg-primary/10 dark:bg-primary/20 px-8 py-4 rounded-xl border-2 border-primary-light dark:border-primary/30 min-w-[300px]">
                  <div className="text-xl font-bold text-neutral-800 dark:text-white">
                    {formatDate(selectedDate)}
                  </div>
                  {isToday && (
                    <div className="text-sm text-primary font-semibold mt-1">{t('superadminpanel.today')}</div>
                  )}
                </div>
                <button 
                  onClick={() => changeDate(1)}
                  className="w-12 h-12 flex items-center justify-center bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-xl transition"
                >
                  →
                </button>
              </div>
            </div>
            
            {loadingBookings ? (
              <div className="text-center py-16">
                <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <div className="text-neutral-600 dark:text-neutral-400">{t('superadminpanel.loadingBookings')}</div>
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-16">
                <AlertCircle size={64} className="mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
                <p className="text-neutral-500 dark:text-neutral-400 text-xl font-semibold">{t('superadminpanel.noBookings')}</p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Customer Bookings Section */}
                {bookings.filter(b => b.createdBy !== 'owner' && b.type !== 'recurring').length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-primary/20 dark:bg-primary/30 rounded-lg flex items-center justify-center">
                        <User size={20} className="text-primary dark:text-primary-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-neutral-800 dark:text-white">{t('superadminpanel.customerBookings')}</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                          {bookings.filter(b => b.createdBy !== 'owner' && b.type !== 'recurring').length} {t('superadminpanel.bookingsCount')}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {bookings
                        .filter(b => b.createdBy !== 'owner' && b.type !== 'recurring')
                        .map((booking) => (
                          <div
                            key={booking.id}
                            className="group p-5 rounded-xl border-2 border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/10"
                          >
                            <div className="flex justify-between items-center flex-wrap gap-4">
                              <div className="flex items-center gap-6 flex-wrap">
                                <div className="flex items-center gap-3 min-w-[140px]">
                                  <div className="w-12 h-12 bg-primary dark:bg-primary-700 rounded-xl flex items-center justify-center">
                                    <Clock size={24} className="text-white" />
                                  </div>
                                  <div>
                                    <div className="font-bold text-neutral-800 dark:text-white text-lg">{booking.startTime}</div>
                                    <div className="text-sm text-neutral-600 dark:text-neutral-400">{booking.endTime}</div>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-neutral-500 dark:text-neutral-400">{t('superadminpanel.stadium')}</div>
                                  <div className="font-semibold text-neutral-800 dark:text-white">{booking.stadiumName}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-neutral-500 dark:text-neutral-400">{t('superadminpanel.customer')}</div>
                                  <div className="font-semibold text-neutral-800 dark:text-white">{booking.customerName}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Phone size={18} className="text-neutral-600 dark:text-neutral-400" />
                                  <span className="text-neutral-700 dark:text-neutral-300">{booking.customerPhone}</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-primary dark:text-primary-400">${booking.price}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Manual Bookings Section */}
                {bookings.filter(b => b.createdBy === 'owner' && b.type !== 'recurring').length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                        <Edit size={20} className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-neutral-800 dark:text-white">{t('superadminpanel.manualBookings')}</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                          {bookings.filter(b => b.createdBy === 'owner' && b.type !== 'recurring').length} {t('superadminpanel.bookingsCount')}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {bookings
                        .filter(b => b.createdBy === 'owner' && b.type !== 'recurring')
                        .map((booking) => (
                          <div
                            key={booking.id}
                            className="group p-5 rounded-xl border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10"
                          >
                            <div className="flex justify-between items-center flex-wrap gap-4">
                              <div className="flex items-center gap-6 flex-wrap">
                                <div className="flex items-center gap-3 min-w-[140px]">
                                  <div className="w-12 h-12 bg-blue-600 dark:bg-blue-700 rounded-xl flex items-center justify-center">
                                    <Clock size={24} className="text-white" />
                                  </div>
                                  <div>
                                    <div className="font-bold text-neutral-800 dark:text-white text-lg">{booking.startTime}</div>
                                    <div className="text-sm text-neutral-600 dark:text-neutral-400">{booking.endTime}</div>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-neutral-500 dark:text-neutral-400">{t('superadminpanel.stadium')}</div>
                                  <div className="font-semibold text-neutral-800 dark:text-white">{booking.stadiumName}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-neutral-500 dark:text-neutral-400">{t('superadminpanel.customer')}</div>
                                  <div className="font-semibold text-neutral-800 dark:text-white">{booking.customerName}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Phone size={18} className="text-neutral-600 dark:text-neutral-400" />
                                  <span className="text-neutral-700 dark:text-neutral-300">{booking.customerPhone}</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">${booking.price}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Recurring Bookings Section */}
                {bookings.filter(b => b.type === 'recurring').length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                        <CalendarDays size={20} className="text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-neutral-800 dark:text-white">{t('superadminpanel.recurringBookings')}</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                          {bookings.filter(b => b.type === 'recurring').length} {t('superadminpanel.bookingsCount')}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {bookings
                        .filter(b => b.type === 'recurring')
                        .map((booking) => (
                          <div
                            key={booking.id}
                            className="group p-5 rounded-xl border-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10"
                          >
                            <div className="flex justify-between items-center flex-wrap gap-4">
                              <div className="flex items-center gap-6 flex-wrap">
                                <div className="flex items-center gap-3 min-w-[140px]">
                                  <div className="w-12 h-12 bg-purple-600 dark:bg-purple-700 rounded-xl flex items-center justify-center">
                                    <CalendarDays size={24} className="text-white" />
                                  </div>
                                  <div>
                                    <div className="font-bold text-neutral-800 dark:text-white text-lg">{booking.startTime}</div>
                                    <div className="text-sm text-neutral-600 dark:text-neutral-400">{booking.endTime}</div>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-neutral-500 dark:text-neutral-400">{t('superadminpanel.stadium')}</div>
                                  <div className="font-semibold text-neutral-800 dark:text-white">{booking.stadiumName}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-neutral-500 dark:text-neutral-400">{t('superadminpanel.label')}</div>
                                  <div className="font-semibold text-purple-700 dark:text-purple-300">{booking.label}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* --- ADD STADIUM MODAL (CORRECTED) --- */}
        {showAddStadium && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl rounded-xl border border-white/20 dark:border-neutral-700/50 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto my-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-neutral-800 dark:text-white">Add New Stadium Client</h2>
                <button 
                  onClick={() => setShowAddStadium(false)}
                  className="p-2 hover:bg-neutral-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Stadium ID <span className="text-red-500">*</span>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-2">(Used in URLs, no spaces)</span>
                  </label>
                  <input
                    type="text"
                    value={newStadium.id}
                    onChange={(e) => setNewStadium({...newStadium, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                    placeholder="green-field-stadium"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Stadium Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newStadium.name}
                      onChange={(e) => setNewStadium({...newStadium, name: e.target.value})}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                      placeholder="Green Field Stadium"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newStadium.location}
                      onChange={(e) => setNewStadium({...newStadium, location: e.target.value})}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                      placeholder="Beirut, Lebanon"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={newStadium.phone}
                      onChange={(e) => setNewStadium({...newStadium, phone: e.target.value})}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                      placeholder="+961 3 123 456"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Owner Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={newStadium.ownerEmail}
                      onChange={(e) => setNewStadium({...newStadium, ownerEmail: e.target.value})}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                      placeholder="owner@example.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Price per Slot ($)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newStadium.pricePerSlot}
                      onChange={(e) => setNewStadium({...newStadium, pricePerSlot: e.target.value})}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Monthly Subscription Fee ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={newStadium.monthlyFee}
                      onChange={(e) => setNewStadium({...newStadium, monthlyFee: e.target.value})}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                <div className="bg-primary/10 dark:bg-primary/20 border border-primary-light dark:border-primary/30 rounded-lg p-4">
                  <h4 className="font-semibold text-primary-dark dark:text-primary-300 mb-2">✨ What happens next (Automated):</h4>
                  <ul className="text-sm text-neutral-700 dark:text-neutral-300 list-disc list-inside space-y-1">
                    <li>Stadium created with default football field and cancellation policy</li>
                    <li>Firebase Authentication account auto-created for owner</li>
                    <li>Secure temporary password generated automatically</li>
                    <li>You'll receive the credentials to share with the owner</li>
                    <li>Owner can customize sports, prices, and photos after login</li>
                  </ul>
                </div>
                
                {/* Show owner credentials if they were just created */}
                {createdOwnerCredentials && (
                  <div className="bg-primary dark:bg-primary-900 border-2 border-primary-600 dark:border-primary-700 rounded-lg p-5 mt-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white dark:bg-neutral-800 rounded-full flex items-center justify-center">
                        <CheckCircle size={28} className="text-primary dark:text-primary-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">Owner Account Created!</h3>
                        <p className="text-sm text-white/90">Send these credentials to the owner</p>
                      </div>
                    </div>
                    
                    <div className="bg-white/10 dark:bg-black/20 backdrop-blur-sm rounded-lg p-4 space-y-3">
                      <div>
                        <label className="text-xs text-white/80 font-semibold uppercase tracking-wide">Stadium</label>
                        <div className="text-white font-bold text-lg">{createdOwnerCredentials.stadiumName}</div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-white/80 font-semibold uppercase tracking-wide">Email</label>
                        <div className="flex items-center gap-2">
                          <code className="text-white font-mono bg-black/20 px-3 py-2 rounded flex-1">
                            {createdOwnerCredentials.email}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(createdOwnerCredentials.email);
                              toast.success('Email copied!');
                            }}
                            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition"
                            title="Copy email"
                          >
                            <Copy size={18} className="text-white" />
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-white/80 font-semibold uppercase tracking-wide">Temporary Password</label>
                        <div className="flex items-center gap-2">
                          <code className="text-white font-mono bg-black/20 px-3 py-2 rounded flex-1 font-bold text-lg">
                            {createdOwnerCredentials.password}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(createdOwnerCredentials.password);
                              toast.success('Password copied!');
                            }}
                            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition"
                            title="Copy password"
                          >
                            <Copy size={18} className="text-white" />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 bg-white/10 dark:bg-black/20 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-sm text-white/90">
                        ⚠️ <strong>Important:</strong> Save this password now! It won't be shown again. 
                        The owner can change it after their first login.
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4 border-t">
                  {!createdOwnerCredentials ? (
                    <>
                      <button
                        onClick={() => setShowAddStadium(false)}
                        className="flex-1 px-4 py-3 border-2 border-neutral-300 dark:border-neutral-600 rounded-lg font-semibold hover:bg-neutral-50 dark:hover:bg-neutral-800 transition text-neutral-800 dark:text-white"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddStadium}
                        className="flex-1 px-4 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition"
                      >
                        Add Stadium Client
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setShowAddStadium(false);
                        setCreatedOwnerCredentials(null);
                      }}
                      className="flex-1 px-4 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition flex items-center justify-center gap-2"
                    >
                      <CheckCircle size={20} />
                      Done - Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- EDIT STADIUM MODAL (CORRECTED) --- */}
        {showEditModal && selectedStadium && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl rounded-xl border border-white/20 dark:border-neutral-700/50 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto my-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-neutral-800 dark:text-white">Edit Stadium: {selectedStadium.name}</h2>
                <button 
                  onClick={handleCloseEditModal}
                  className="p-2 hover:bg-neutral-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Stadium ID (Cannot be changed)
                  </label>
                  <input
                    type="text"
                    value={selectedStadium.id}
                    disabled
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg bg-neutral-100 cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Stadium Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={selectedStadium.name}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={selectedStadium.location}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={selectedStadium.phone}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Owner Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="ownerEmail"
                      value={selectedStadium.ownerEmail}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Monthly Subscription Fee ($)
                    </label>
                    <input
                      type="number"
                      name="monthlyFee"
                      min="0"
                      value={selectedStadium.monthlyFee || 0}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Status
                    </label>
                    <select
                      name="status"
                      value={selectedStadium.status}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary"
                    >
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Admin Note:</h4>
                  <p className="text-sm text-blue-700">
                    Editing "Sports & Courts" or "Photos" must be done by the stadium owner through their dashboard.
                  </p>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  {saveState === 'idle' && (
                    <>
                      <button
                        onClick={handleCloseEditModal}
                        className="flex-1 px-4 py-3 border-2 border-neutral-300 rounded-lg font-semibold hover:bg-neutral-50 transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateStadium}
                        className="flex-1 px-4 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition"
                      >
                        Save Changes
                      </button>
                    </>
                  )}
                  {saveState === 'saving' && (
                    <button
                      disabled
                      className="flex-1 px-4 py-3 bg-primary/70 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
                    >
                      <Loader size={20} className="animate-spin" />
                      Saving...
                    </button>
                  )}
                  {saveState === 'success' && (
                    <button
                      disabled
                      className="flex-1 px-4 py-3 bg-primary text-white rounded-lg font-semibold flex items-center justify-center gap-2"
                    >
                      <Check size={20} />
                      Saved Successfully!
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminPanel;